import { http } from '../http'
import { unwrap, type ApiEnvelope } from '../envelope'
import { idempotencyHeaders, newIdempotencyKey } from '../idempotency'

export type ActorType = 'PLATFORM_ADMIN' | 'FINANCIAL_INSTITUTION' | 'ANCHOR' | 'SUPPLIER'

/** One organisation link the user can sign in as. */
export interface LoginScope {
  actorType: ActorType
  /** absent for PLATFORM_ADMIN */
  orgId?: string
  orgName?: string
}

export interface LoginRequest {
  email: string
  password: string
  /** only on the second call, after the first returned scopeSelectionRequired */
  actorType?: ActorType
  orgId?: string
}

export interface LoginResponse {
  /** null when scopeSelectionRequired */
  accessToken: string | null
  email: string
  fullName: string
  /** backend permission codes (RESOURCE:ACTION); empty when scopeSelectionRequired */
  permissions: string[]
  actorType: ActorType | null
  orgId: string | null
  /** the user has more than one active org link and must pick one of availableScopes */
  scopeSelectionRequired: boolean
  availableScopes: LoginScope[]
}

/**
 * Credential endpoints. None of them carry a bearer token or trigger 401 → refresh → retry.
 * POST /auth/login is the only auth endpoint the backend has today: there is no refresh,
 * logout or password-reset endpoint yet.
 */
export const authApi = {
  login: (body: LoginRequest) =>
    http
      .post<ApiEnvelope<LoginResponse>>('/auth/login', body, { skipAuthRefresh: true })
      .then(unwrap),
  /** Always resolves the same way whether or not the account exists (no enumeration). */
  forgotPassword: (identifier: string) =>
    http
      .post<void>(
        '/auth/password/forgot',
        { identifier },
        { skipAuthRefresh: true, headers: idempotencyHeaders(newIdempotencyKey()) },
      )
      .then(() => undefined),
  resetPassword: (token: string, password: string) =>
    http
      .post<void>(
        '/auth/password/reset',
        { token, password },
        { skipAuthRefresh: true, headers: idempotencyHeaders(newIdempotencyKey()) },
      )
      .then(() => undefined),
}
